import React, { useEffect, useState } from 'react'
import { CFormInput, CFormLabel, CButton, CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle, CPopover, CFormTextarea } from '@coreui/react'
import { Info, Plus } from 'lucide-react'
import CustomDatePickingFormat from './CustomDatePickingFormat'
import Editor, { Toolbar } from 'react-simple-wysiwyg'

const questions = ['1. Any secondary medical diagnosis', '2. Ambulatory aids', '3. Does patient have an IV inserted', '4. Gait', '5. Patient is own self-assessment of his her own ability to ambulation', '6. History of falling within the last 6 months']

const questionsWithInfo = [
  {
    question: 'History of Falling',
    info: ['No: score 0', 'Yes: score 15'],
    description: `<span>25 points for a fall in the present hospital admission or a recent fall.</span>`,
  },
  {
    question: 'Secondary Diagnoses',
    info: ['No: score 0', 'Yes: score 25'],
    description: `<span>15 points for more than one medical diagnosis.</span>`,
  },
  {
    question: 'Ambulatory Aid',
    info: ['Bedrest: score 0', 'Uses wheelchair: score 0', 'Nurse assist: score 0', 'Use of crutches, cane or walker: score 15', 'Ambulates holding onto the furniture: score 30'],
    description: `<span>0 points for no aid (nurse assist, bed rest, wheelchair), 15 points for crutches, cane, or walker, 30 points for grasping furniture for support to walk.</span>`,
  },
  {
    question: 'Intravenous Therapy',
    info: ['No: score 0', 'Yes: score 20'],
    description: `<span>20 points for an intravenous therapy or heparin lock.</span>`,
  },
  {
    question: 'Gait/Transferring',
    info: ['Independent: score 0', 'Shuffling gait/no assist: score 10', 'Bedridden or uses aid: score 20'],
    description: `<span>0 points for a normal gait, 10 points for a weak gait, 20 points for an impaired gait.</span>`,
  },
  {
    question: 'Mental Status',
    info: ['Can ambulate with no assistive devices or cannot due to limitation but understands: score 0', 'Uses assistive device or bedridden but says no issue ambulating: score 15'],
    description: `<span>0 points for being oriented to one's own ability.</span>`,
  },
]

const MorseScore = ({ noteStructureRef, heading,saveTreatsShell, showDataIfNoteIsFollowUp = true, showTreatmentAsAccordion, diagnosisName, scoreType, updateNoteStructure, type = 'note', setNoteRefTrigger }) => {
  const today = new Date()
  const formattedDate = `${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}/${today.getFullYear()}`

  const [scores, setScores] = useState(['0', '0', '0', '0', '0', '0', '0'])
  const [average, setAverage] = useState(0)
  const [date, setDate] = useState(formattedDate)

  const [modalVisible, setModalVisible] = useState(false)
  const [modalScores, setModalScores] = useState(['0', '0', '0', '0', '0', '0'])
  const [modalAverage, setModalAverage] = useState(0)

  const [showEditHtml, setShowEditHtml] = useState(false);
  const [tempHtmlContent, setTempHtmlContent] = useState('');



  useEffect(() => {
    const existing = noteStructureRef.current?.[scoreType]?.[diagnosisName]
    if (existing) {
      const normalizedScores = Array.isArray(existing.score)
        ? existing.score.map((value) => (value === '' || value === null || value === undefined ? '0' : String(value)))
        : ['0', '0', '0', '0', '0', '0', '0']
      setScores(normalizedScores)
      setDate(existing.date || formattedDate)
      const total = normalizedScores.slice(0, 6).reduce((sum, v) => sum + Number(v || 0), 0)
      setAverage(total)
    }
  }, [diagnosisName, scoreType])

  useEffect(() => {
    const existing = noteStructureRef.current?.[scoreType]?.[diagnosisName]
    if (existing) {
      noteStructureRef.current = {
        ...noteStructureRef.current,
        [scoreType]: {
          ...noteStructureRef.current[scoreType],
          [diagnosisName]: {
            ...existing,
            finalScore: average,
          },
        },
      }
    }
  }, [average])

  const handleScoreChange = (newScores) => {
    updateNoteStructure(newScores, date, diagnosisName, scoreType)
    setNoteRefTrigger((prev) => prev + 1)
  }

  const handleDateChange = (newDate) => {
    setDate(newDate)
    updateNoteStructure(scores, newDate, diagnosisName, scoreType)
    setNoteRefTrigger((prev) => prev + 1)
  }

  const handleModalScoreChange = (index, value) => {
    const sanitizedValue = value.replace(/[^0-9]/g, '')
    const newScores = [...modalScores]
    newScores[index] = sanitizedValue === '' ? '' : String(Number(sanitizedValue))
    setModalScores(newScores)
    const total = newScores.reduce((sum, val) => sum + Number(val || 0), 0)
    setModalAverage(total)
  }

  const handleModalSave = () => {
    const numericScores = modalScores.map((value) => Number(value || 0))
    const finalScores = [...numericScores, modalAverage]
    setScores(finalScores.map((value) => String(value)))
    setAverage(modalAverage)
    handleScoreChange(finalScores)
    setModalVisible(false)
  }

  const openModal = () => {
    const initial = scores
      .slice(0, 6)
      .map((value) => (value === '' || value === null || value === undefined ? '0' : String(value)))
    const total = initial.reduce((sum, val) => sum + Number(val || 0), 0)
    setModalScores(initial)
    setModalAverage(total)
    setModalVisible(true)
  }

  return (
    <>
      {heading && (
        <h6 style={{ fontSize: '14px' }}>
          <b>{heading}</b>
        </h6>
      )}

      {!showTreatmentAsAccordion && (
        <div>
          <div className="d-flex align-items-center gap-2">
            <b>Morse Score</b>
            <CFormLabel
              className="patient-note-action d-flex align-items-center justify-content-center shadow"
              onClick={openModal}
              style={{
                width: '30px',
                height: '30px',
                borderRadius: '20px',
                background: 'var(--ui-color-primary, #2b7cd3)',
                cursor: 'pointer',
              }}
            >
              <Plus className="patient-note-action-icon" strokeWidth={2} size={15} />
            </CFormLabel>
          </div>
          <hr />
        </div>
      )}

      {/* {noteStructureRef?.current?.freeTypesEverything?.MorseScore?.[diagnosisName] && <div className='my-2'>
        <div
          onClick={() => {
            setTempHtmlContent(noteStructureRef?.current?.freeTypesEverything?.MorseScore?.[diagnosisName])
            setShowEditHtml(true)
          }}
          className='p-4'
          style={{ background: "#f6f6f6ff", borderRadius: "10px", cursor: "text" }}
          dangerouslySetInnerHTML={{ __html: noteStructureRef?.current?.freeTypesEverything?.MorseScore?.[diagnosisName] }}
        ></div>
      </div>} */}
      {noteStructureRef?.current?.freeTypesEverything?.MorseScore?.[diagnosisName] && (
        <div className="my-2">
          <div
            className="p-2 patient-theme-old-data patient-theme-old-data--readonly patient-theme-old-data--final-note"
            dangerouslySetInnerHTML={{
              __html:
                noteStructureRef?.current?.freeTypesEverything?.MorseScore?.[diagnosisName],
            }}
          ></div>
        </div>
      )}

      {date && (
        <div className="d-flex gap-5 ms-1">
          {showTreatmentAsAccordion ? (
            <div className="d-flex gap-2">
              <CFormLabel>
                <strong>Score:</strong>
              </CFormLabel>
              <p>{average || '-'}</p>
              <CFormLabel>
                <strong>Date:</strong>
              </CFormLabel>
              <p>{date || '-'}</p>
            </div>
          ) : (
            <div className="d-flex flex-column border rounded p-3 bg-light bg-secondary w-100">
              {
                noteStructureRef.current?.[scoreType]?.[diagnosisName] && (
                  <p className="p-0 m-0">
                    Morse Score: {average}{' '}
                    <span className="" style={{ marginRight: '10px' }}>
                      {'   '}
                    </span>{' '}
                    Date: {new Date(date).toLocaleDateString('en-US')}
                  </p>
                )
                // <CFormTextarea
                //   disabled
                //   rows={2}
                //   value={`Morse Score: ${average}  Date: ${new Date(date).toLocaleDateString('en-US')}`}
                //   style={{
                //     fontSize: "14px",
                //     backgroundColor: "#f8f9fa",
                //     border: 'none',
                //     color: "#495057",
                //   }}
                //   />
              }
            </div>
          )}
        </div>
      )}

      <CModal className="patient-theme-modal patient-note-form-modal assessment-theme__modal" backdrop="static" size="lg" visible={modalVisible} onClose={() => setModalVisible(false)} scrollable>
        <CModalHeader>
          <CModalTitle>Morse Score</CModalTitle>
        </CModalHeader>

        <CModalBody>
          <div className="px-4 py-3">
            <div className="mb-4 d-flex align-items-center gap-3 " style={{ width: '40%' }}>
              <CFormLabel className="fw-medium mb-0">Date:</CFormLabel>
              <CustomDatePickingFormat
                type="date"
                value={date}
                onChange={handleDateChange}
                disabled={type !== 'note'}
                style={{
                  width: '180px',
                  height: '32px',
                  fontSize: '14px',
                  padding: '4px 8px',
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }} className="w-100">
              {questionsWithInfo.map((item, index) => (
                <div className="shadow-sm bg-secondary p-4 rounded-2" key={index}>
                  <div className="d-flex align-items-center gap-1">
                    <p style={{ fontSize: '14px' }} className="m-0">
                      {index + 1}. {item.question}
                    </p>
                    {/* <CPopover
                      content={
                        <ul className="p-0 m-0 ps-2">
                          {item.info.map((opt, i) => (
                            <li key={i}>{opt}</li>
                          ))}
                        </ul>
                      }
                      placement="right"
                      trigger="hover"
                    >
                      <CButton className="p-0 m-0" color="none" size="sm">
                        <Info size={16} />
                      </CButton>
                    </CPopover> */}
                  </div>
                  <span className="d-flex p-1 text-warning" dangerouslySetInnerHTML={{ __html: item.description }} />
                  <CFormInput
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={modalScores[index] === '' ? '' : modalScores[index]}
                    onChange={(e) => handleModalScoreChange(index, e.target.value)}
                    onFocus={(e) => {
                      if (e.target.value === '0') {
                        handleModalScoreChange(index, '')
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '' || e.target.value === null) {
                        handleModalScoreChange(index, '0')
                      }
                    }}
                    style={{
                      width: '200px',
                      marginTop: '10px',
                      height: '30px',
                      fontSize: '13px',
                      padding: '4px 6px',
                    }}
                  />
                </div>
              ))}
            </div>

            <div className="text-center mt-4 pt-2 border-top">
              <h6 className="fw-semibold">
                Total Score: <span>{modalAverage}</span>
              </h6>
            </div>
          </div>
        </CModalBody>

        <CModalFooter>
          <CButton variant="outline" color="secondary" onClick={() => setModalVisible(false)}>
            Close
          </CButton>
          <CButton color="primary" onClick={handleModalSave}>
            Save
          </CButton>
        </CModalFooter>
      </CModal>

            <CModal
              className="patient-theme-modal patient-note-form-modal assessment-theme__modal"
              size="lg"
              visible={showEditHtml}
              onClose={() => setShowEditHtml(false)}
              aria-labelledby="OptionalSizesExample2"
            >
              <CModalHeader>
                <CModalTitle id="OptionalSizesExample2">
                  Edit Morse Score Free Type
                </CModalTitle>
              </CModalHeader>
              <CModalBody>
                <Editor
                  onChange={(e) => {
                    setTempHtmlContent(e.target.value);
                  }}
                        containerProps={{
                          style: {
                            width: '100%',
                            background: 'var(--ui-bg-inset, #161616)',
                            border: 0,
                            height: "100%",
                            minHeight: "unset"
                    },
                  }}
                  value={tempHtmlContent}
                >
                  <tooltip />
                </Editor>
              </CModalBody>
              <CModalFooter>
                <CButton
                  variant="outline"
                  color="secondary"
                  onClick={() => setShowEditHtml(false)}
                >
                  Close
                </CButton>
                <CButton
                  color="primary"
                  onClick={() => {
                    noteStructureRef.current = {
                      ...noteStructureRef.current,
                      freeTypesEverything: {
                        ...noteStructureRef.current?.freeTypesEverything,
                        MorseScore: {
                          ...noteStructureRef.current?.freeTypesEverything?.MorseScore,
                          [diagnosisName]: tempHtmlContent,
                        }
                      },
                    };
                    saveTreatsShell(false);
                    setShowEditHtml(false)
                  }}
                >
                  Save Changes
                </CButton>
              </CModalFooter>
            </CModal>
      
    </>
  )
}

export default MorseScore
